import { useCallback, useEffect, useRef, useState } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router';

const LISTMANAGER_UNSAVED_GUARD = 'listmanager-unsaved-guard';

export type PopStateBlock = {
  isBlocked: boolean;
  proceed: () => void;
  reset: () => void;
};

export function useUnsavedChangesBlocker(isDirty: boolean) {
  const hasPushedGuardRef = useRef(false);
  const [popStateBlocked, setPopStateBlocked] = useState(false);
  const [rePushGuard, setRePushGuard] = useState(0);
  const popStateDeltaRef = useRef(-1);

  const popStateBlock: PopStateBlock = {
    isBlocked: popStateBlocked,
    proceed: useCallback(() => {
      const delta = popStateDeltaRef.current;
      setPopStateBlocked(false);
      window.history.go(delta);
    }, []),
    reset: useCallback(() => {
      setPopStateBlocked(false);
      hasPushedGuardRef.current = false;
      setRePushGuard((n) => n + 1);
    }, []),
  };

  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- push a history guard while the form is dirty */
  useEffect(() => {
    if (!isDirty) {
      hasPushedGuardRef.current = false;
      return;
    }
    if (hasPushedGuardRef.current) return;
    hasPushedGuardRef.current = true;
    const url =
      window.location.pathname +
      window.location.search +
      window.location.hash;
    window.history.pushState(
      { [LISTMANAGER_UNSAVED_GUARD]: true },
      '',
      url
    );
  }, [isDirty, rePushGuard]);

  const protectedPathRef = useRef<string | null>(null);
  if (isDirty) {
    // eslint-disable-next-line react-hooks/refs -- remember the path we are protecting
    protectedPathRef.current = window.location.pathname;
  } else {
    // eslint-disable-next-line react-hooks/refs -- clear the protected path when clean
    protectedPathRef.current = null;
  }

  const allowNextNavigationRef = useRef(false);
  const allowNextNavigation = useCallback(() => {
    allowNextNavigationRef.current = true;
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (!isDirty || protectedPathRef.current === null) return;
      if (window.location.pathname === protectedPathRef.current) {
        setPopStateBlocked(true);
        popStateDeltaRef.current = -1;
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isDirty]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation, historyAction }) => {
      if (historyAction === 'POP') {
        return false;
      }
      if (
        isDirty &&
        currentLocation.pathname !== nextLocation.pathname &&
        allowNextNavigationRef.current
      ) {
        allowNextNavigationRef.current = false;
        return false;
      }
      return (
        isDirty && currentLocation.pathname !== nextLocation.pathname
      );
    }
  );

  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (isDirty) {
          event.preventDefault();
        }
      },
      [isDirty]
    )
  );

  return { blocker, popStateBlock, allowNextNavigation };
}
